import React, { useState } from 'react';
import { MapPin, Search, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCitiesFromDatabase, useAllCitiesCount } from '@/hooks/useCitiesFromDatabase';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const CitiesListPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const ITEMS_PER_PAGE = 150;

  const { data: totalCitiesCount } = useAllCitiesCount();
  const { data, isLoading, error } = useCitiesFromDatabase({
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    searchTerm: searchTerm
  });

  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <>
      <Helmet>
        <title>Cities Directory - Find Therapists Worldwide | Global Therapy Hub</title>
        <meta name="description" content={`Browse ${totalCitiesCount || '30,000+'} cities worldwide to find certified psychologists and therapists. Comprehensive mental health directory with professionals in every major city.`} />
        <meta name="keywords" content="therapist by city, psychologist directory, mental health cities, therapy locations, find therapist near me" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Find Therapists by City
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-6">
            Browse our comprehensive directory of {totalCitiesCount?.toLocaleString() || '30,000+'} cities worldwide. 
            Find certified psychologists, psychiatrists, and therapists in your city or any location globally.
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">{totalCitiesCount?.toLocaleString() || '32,719'}</div>
              <div className="text-sm text-muted-foreground">Cities</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">195+</div>
              <div className="text-sm text-muted-foreground">Countries</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">10,000+</div>
              <div className="text-sm text-muted-foreground">Therapists</div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
            <Input
              type="text"
              placeholder="Search cities or countries..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 h-12 text-lg"
            />
          </div>
        </div>

        {/* Results Info */}
        {data && (
          <div className="mb-6 text-center">
            <p className="text-muted-foreground">
              Showing {data.cities.length} cities (page {currentPage} of {data.totalPages})
              {searchTerm && ` for "${searchTerm}"`}
            </p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-destructive">Error loading cities. Please try again.</p>
          </div>
        )}

        {/* Cities Grid */}
        {data && data.cities.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {data.cities.map((city) => (
              <Card key={city.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-start gap-2 text-lg">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div>{city.name}</div>
                      {city.state_province && (
                        <div className="text-sm font-normal text-muted-foreground">{city.state_province}</div>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary">{city.country}</Badge>
                    <Link to={`/city/${city.slug}`}>
                      <Button size="sm" variant="outline">
                        View Therapists
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {data && data.cities.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No cities found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search terms.
            </p>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              {currentPage > 2 && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setCurrentPage(1)}>1</Button>
                  {currentPage > 3 && <span className="text-muted-foreground">...</span>}
                </>
              )}
              
              {currentPage > 1 && (
                <Button variant="ghost" size="sm" onClick={() => setCurrentPage(currentPage - 1)}>
                  {currentPage - 1}
                </Button>
              )}
              
              <Button variant="default" size="sm">{currentPage}</Button>
              
              {currentPage < data.totalPages && (
                <Button variant="ghost" size="sm" onClick={() => setCurrentPage(currentPage + 1)}>
                  {currentPage + 1}
                </Button>
              )}
              
              {currentPage < data.totalPages - 1 && (
                <>
                  {currentPage < data.totalPages - 2 && <span className="text-muted-foreground">...</span>}
                  <Button variant="ghost" size="sm" onClick={() => setCurrentPage(data.totalPages)}>
                    {data.totalPages}
                  </Button>
                </>
              )}
            </div>
            
            <Button
              variant="outline"
              onClick={() => setCurrentPage(p => Math.min(data.totalPages, p + 1))}
              disabled={currentPage === data.totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default CitiesListPage;
